import styled, { css, keyframes, type CSSObject } from 'styled-components'
import { TRANSPARENCY_GRID_STYLE } from '@messenger/ui/components/compare-slider'
import colors from 'utils/styling/colors'

/**
 * Rail entry appended to Pintura's util tab list. It reuses Pintura's own
 * `.PinturaNavMain button` rules (size, radius, hover ring, selected fill)
 * by living inside the same list, so it matches Crop/Filter/Adjust exactly.
 */
export const RailTab = styled.li`
  display: flex;

  button {
    color: inherit;
    background: none;
    border: 0;
    font: inherit;
    /* Pintura clips long tab labels with an ellipsis; this label is two
       words, so let it wrap onto a second line instead. */
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    line-height: 1.15;
  }

  /* Pintura clamps the label span to a single clipped line with its own
     higher-specificity rule, so these need !important. "Background" is one
     word; at 10px it spans the tab with a little air on each side. */
  button span {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    max-width: none !important;
    width: 100% !important;
    font-size: 10px !important;
  }

  /* Pintura sizes rail icons by width; drop the SVG's own height attribute
     so the icon keeps its square ratio instead of stretching the tab. */
  svg {
    height: auto;
    fill: currentColor;
  }
`

/**
 * Covers Pintura's stage and toolbar while the Remove Background tool is
 * open, leaving the util rail visible so the person can switch tools. The
 * rail is lifted above the overlay from `Pintura.styled`.
 */
export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 24px;
  box-sizing: border-box;
  /* The Pintura palette entries are bare RGB triplets (Pintura's own CSS
     variables want them that way), so they need rgb() to be valid here. */
  background-color: rgb(${colors('pinturaBackground')});
  color: rgb(${colors('pinturaForeground')});

  /* In Pintura's landscape layout the util rail sits at left: 1em, 4em wide. */
  .PinturaRoot[data-env~='landscape'] > & {
    padding-left: 6.5em;
  }
`

// The same checkerboard the compare slider draws, so a cutout's alpha reads
// identically on the review and accepted screens.
const transparencyGrid = css(TRANSPARENCY_GRID_STYLE as CSSObject)

/**
 * Bounds the image or compare slider so it stays inside the modal. It has
 * a definite height (a share of the overlay) so children can size with
 * percentages and portrait photos are letterboxed instead of overflowing.
 */
export const Stage = styled.div<{ $heightShare?: string }>`
  position: relative;
  width: 100%;
  max-width: 720px;
  height: ${({ $heightShare }) => $heightShare ?? '60%'};
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

const pulse = keyframes`
  from { opacity: 0.5; }
  to { opacity: 1; }
`

/**
 * The photo while the model works: eases between half and full opacity so
 * the wait reads as "in progress" without a spinner covering the image.
 * People who asked for reduced motion get a steady, dimmed photo instead.
 */
export const RemovingImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  animation: ${pulse} 1.4s ease-in-out infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.7;
  }
`

/** Static photo shown on the dialog-like steps (disclosure, error, previous). */
export const Thumbnail = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
`

/**
 * Result preview with a checkerboard behind it so the cutout's alpha shows.
 * Sized by height so the frame shrink-wraps the photo and the badges pinned
 * to its corners sit on the image, not on empty stage.
 */
export const ResultFrame = styled.div`
  position: relative;
  display: inline-flex;
  height: 100%;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  ${transparencyGrid}

  img {
    display: block;
    height: 100%;
    width: auto;
    max-width: 100%;
    object-fit: contain;
  }
`

/** Round check badge pinned bottom-right of the accepted result. */
export const CheckBadge = styled.span<{ $nudged?: boolean }>`
  position: absolute;
  right: 12px;
  bottom: ${({ $nudged }) => ($nudged ? '48px' : '12px')};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors('brand')};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
`

/** "Edited with AI" pill on the accepted result. */
export const AiTag = styled.span`
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
  background-color: rgba(0, 0, 0, 0.7);
`

/** Floating "Accept changes?" prompt over the compare slider. */
export const AcceptChip = styled.div`
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 8px 16px;
  border-radius: 999px;
  background-color: rgba(28, 33, 37, 0.92);
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  white-space: nowrap;
`

export const ChipButton = styled.button<{ $accept?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $accept }) =>
    $accept ? colors('brand') : 'rgba(255, 255, 255, 0.16)'};
  transition:
    background-color 0.15s ease-out,
    filter 0.15s ease-out;

  &:hover {
    /* The accept button darkens the brand colour; the discard one lightens. */
    filter: ${({ $accept }) => ($accept ? 'brightness(0.85)' : 'none')};
    background-color: ${({ $accept }) =>
      $accept ? colors('brand') : 'rgba(255, 255, 255, 0.28)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }
`

export const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  color: inherit;
`

export const Price = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${colors('secondary')};
`

export const Body = styled.p`
  margin: 0;
  max-width: 480px;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  color: inherit;
`

export const Actions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`
